import { useState, useEffect, useCallback, useRef } from 'react';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import TransactionTable from './TransactionTable';
import MapDVF from '@/components/maps/MapDVF';
import MapDVFHeatmap from '@/components/maps/MapDVFHeatmap';
import { rechercheDVF } from '@/api/geo';
import { etudesMarcheApi } from '@/api';
import {
  ETAT_MARCHE_OPTIONS,
  EVOLUTION_PRIX_OPTIONS,
  SELECTION_QUARTILE_OPTIONS,
} from '@/constants/enums';

function formatPrix(value) {
  if (value == null) return '—';
  return Math.round(value).toLocaleString('fr-FR');
}

function ChartImage({ src, alt, fullWidth = false }) {
  if (!src) return null;
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 ${fullWidth ? 'col-span-full' : ''}`}>
      <img src={src} alt={alt} className="w-full h-auto" loading="lazy" />
    </div>
  );
}

function DvfKpiCards({ kpis }) {
  if (!kpis) return null;
  const items = [
    { label: 'Nb. pièces moyen', value: kpis.pieces, unit: '' },
    { label: 'Surface bâtie moy.', value: kpis.surface, unit: 'm²' },
    { label: 'Superficie moy.', value: kpis.superficie, unit: 'm²' },
  ];
  return (
    <div className="grid grid-cols-3 gap-4">
      {items.map(({ label, value, unit }) => (
        <div key={label} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-900">
            {value === '-' ? '—' : `${value} ${unit}`}
          </p>
        </div>
      ))}
    </div>
  );
}

const LOYER_TYPE_LABELS = {
  appartement: 'Appartement',
  appartement_t1t2: 'T1 - T2',
  appartement_t3plus: 'T3+',
  maison: 'Maison',
};

function LoyersCards({ data }) {
  if (!data?.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {data.map((item) => {
        const fiable = item.r2_ajuste >= 0.5 && item.nb_observations_commune >= 30;
        const moyen = item.r2_ajuste >= 0.3 && item.nb_observations_commune >= 10;
        const badgeClass = fiable
          ? 'bg-green-100 text-green-700'
          : moyen
            ? 'bg-amber-100 text-amber-700'
            : 'bg-red-100 text-red-700';
        const badgeLabel = fiable ? 'Fiable' : moyen ? 'Indicatif' : 'Peu fiable';

        return (
          <div key={item.type_logement} className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-sm font-medium text-slate-500 mb-1">
              {LOYER_TYPE_LABELS[item.type_logement] || item.type_logement}
            </p>
            <p className="text-2xl font-bold text-slate-900">
              {item.loyer_m2.toFixed(1).replace('.', ',')} <span className="text-sm font-normal text-slate-500">€/m²</span>
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {item.loyer_m2_bas.toFixed(1).replace('.', ',')} — {item.loyer_m2_haut.toFixed(1).replace('.', ',')} €/m²
            </p>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
              {badgeLabel} ({item.nb_observations_commune.toLocaleString('fr-FR')} obs.)
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DvfStatsCards({ statsCommune, statsVoisinage, comparaison }) {
  if (!statsCommune?.prix_m2_median) return null;

  const diffPct = comparaison?.diff_pct;
  const badgeColor =
    diffPct > 3 ? 'bg-red-100 text-red-700' :
    diffPct < -3 ? 'bg-green-100 text-green-700' :
    'bg-slate-100 text-slate-700';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Commune */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h4 className="text-sm font-medium text-slate-500 mb-3">
          Commune {statsCommune.commune ? `— ${statsCommune.commune}` : ''}
        </h4>
        <p className="text-2xl font-bold text-slate-900">
          {formatPrix(statsCommune.prix_m2_median)} <span className="text-base font-normal text-slate-500">€/m² médian</span>
        </p>
        <div className="mt-2 space-y-1 text-sm text-slate-600">
          <p>{formatPrix(statsCommune.nb_mutations)} mutations</p>
          {statsCommune.surface_moyenne && (
            <p>{formatPrix(statsCommune.surface_moyenne)} m² surface moy.</p>
          )}
          {statsCommune.date_premiere_mutation && statsCommune.date_derniere_mutation && (
            <p className="text-xs text-slate-400">
              {statsCommune.date_premiere_mutation} → {statsCommune.date_derniere_mutation}
            </p>
          )}
        </div>
      </div>

      {/* Voisinage */}
      {statsVoisinage && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h4 className="text-sm font-medium text-slate-500 mb-3">Voisinage (2 km)</h4>
          <p className="text-2xl font-bold text-slate-900">
            {formatPrix(statsVoisinage.prix_m2_median)} <span className="text-base font-normal text-slate-500">€/m² médian</span>
          </p>
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <p>{statsVoisinage.nb_mutations} mutations</p>
            {comparaison && (
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                {comparaison.diff_pct > 0 ? '+' : ''}{comparaison.diff_pct}% — {comparaison.qualite}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SectionMarche({ data, onChange, onRefresh, latitude, longitude, codeInsee, typeBien, analyseMarqueTexte, onChangeAnalyseMarqueTexte, loyersData, expertiseId }) {
  const val = (field) => data?.[field] ?? '';
  const handleChange = (field) => (e) => onChange(field, e.target.value);

  const [dvfData, setDvfData] = useState(null);
  const creatingEtudeRef = useRef(false);
  const fetchingRef = useRef(false);
  const etudeIdRef = useRef(data?.id);
  etudeIdRef.current = data?.id;

  const fetchDvf = useCallback(async ({ refresh = false } = {}) => {
    if (!codeInsee || !typeBien) return;
    if (fetchingRef.current && !refresh) return;
    fetchingRef.current = true;

    // Auto-créer l'étude de marché si nécessaire
    let etudeId = etudeIdRef.current;
    if (!etudeId && expertiseId) {
      if (creatingEtudeRef.current) {
        fetchingRef.current = false;
        return;
      }
      creatingEtudeRef.current = true;
      try {
        const created = await etudesMarcheApi.create({ expertise_id: expertiseId });
        etudeId = created.id;
        etudeIdRef.current = etudeId;
        onRefresh();
      } catch {
        creatingEtudeRef.current = false;
        fetchingRef.current = false;
        return;
      }
    }

    try {
      const result = await rechercheDVF({
        code_insee: codeInsee,
        type_bien: typeBien,
        lat: latitude,
        lon: longitude,
        ...(etudeId ? { etude_marche_id: etudeId } : {}),
        ...(refresh ? { refresh: true } : {}),
      });
      setDvfData(result);
      if (result?.analyse_marche_texte && !data?.analyse_marche) {
        onChange('analyse_marche', result.analyse_marche_texte);
      }
      // Auto-fill quartiles + panel si pas encore renseignés
      if (result?.quartiles && !data?.quartile_1_min) {
        const q = result.quartiles;
        onChange('quartile_1_min', q.quartile_1_min);
        onChange('quartile_1_max', q.quartile_1_max);
        onChange('quartile_2_min', q.quartile_2_min);
        onChange('quartile_2_max', q.quartile_2_max);
        onChange('quartile_3_min', q.quartile_3_min);
        onChange('quartile_3_max', q.quartile_3_max);
        if (!data?.panel_nombre_transactions) {
          onChange('panel_nombre_transactions', q.panel_nombre_transactions);
          onChange('panel_date_debut', q.panel_date_debut);
          onChange('panel_date_fin', q.panel_date_fin);
          onChange('panel_rayon_km', q.panel_rayon_km);
        }
      }
      // Recharger l'expertise pour récupérer les transactions persistées en BDD
      // (seulement si le store n'a pas encore de transactions, ou si refresh explicite)
      if (etudeId && result?.mutations?.length && (refresh || !(data?.transactions?.length))) {
        onRefresh();
      }
    } catch {
      setDvfData(null);
    } finally {
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeInsee, typeBien, expertiseId]);

  useEffect(() => {
    fetchDvf();
  }, [fetchDvf]);

  return (
    <div className="space-y-6">

      {/* ══════ ZONE A — Dashboard DVF CAREX ══════ */}
      {dvfData?.stats_commune?.prix_m2_median && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">Données de marché (DVF)</h3>

          <DvfStatsCards
            statsCommune={dvfData.stats_commune}
            statsVoisinage={dvfData.stats_voisinage}
            comparaison={dvfData.comparaison}
          />

          {/* KPIs moyens */}
          {dvfData.charts?.kpis && <DvfKpiCards kpis={dvfData.charts.kpis} />}

          {/* Graphiques DVF (PNG rendus par Plotly) */}
          {dvfData.charts_images && (
            <div className="space-y-4">
              <ChartImage src={dvfData.charts_images.selling_price_seg} alt="Évolution prix/m² (P25/P50/P75)" fullWidth />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChartImage src={dvfData.charts_images.yearly_sells} alt="Volume de ventes annuel" />
                <ChartImage src={dvfData.charts_images.trend_price} alt="Tendance prix moyen/m²" />
                <ChartImage src={dvfData.charts_images.sell_price_m2} alt="Distribution prix/m²" />
                <ChartImage src={dvfData.charts_images.count_per_quarter} alt="Distribution prix" />
                <ChartImage src={dvfData.charts_images.sell_area_segment} alt="Distribution surfaces" />
                <ChartImage src={dvfData.charts_images.avg_price_area} alt="Corrélation prix/surface" />
              </div>

              <ChartImage src={dvfData.charts_images.mutation_around} alt="Distance vs prix/m²" fullWidth />
            </div>
          )}
        </section>
      )}

      {/* Carte des prix DVF */}
      {latitude && longitude && codeInsee && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">Carte des prix (DVF)</h3>
          <MapDVF latitude={latitude} longitude={longitude} codeInsee={codeInsee} typeBien={typeBien} />
        </section>
      )}

      {/* Heatmap DVF */}
      {latitude && longitude && dvfData?.charts?.heatmap?.clusters?.length > 0 && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">Heatmap des prix (DVF)</h3>
          <MapDVFHeatmap
            latitude={latitude}
            longitude={longitude}
            clusters={dvfData.charts.heatmap.clusters}
            config={dvfData.charts.heatmap.config}
          />
        </section>
      )}


      {/* ══════ Loyers de référence (Carte des Loyers ANIL) ══════ */}
      {loyersData?.data?.length > 0 && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Loyers de référence — {loyersData.nom_commune} ({loyersData.data[0]?.annee})
          </h3>
          <LoyersCards data={loyersData.data} />
        </section>
      )}

      {/* ══════ Analyse de marché auto-générée ══════ */}
      {analyseMarqueTexte && (
        <section className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Analyse de marché (auto-généré)</label>
            <textarea
              className="w-full rounded-lg border-slate-300 bg-slate-50 text-sm"
              rows={4}
              value={analyseMarqueTexte}
              onChange={(e) => onChangeAnalyseMarqueTexte(e.target.value)}
            />
          </div>
        </section>
      )}

      {/* ══════ ZONE B — Analyse qualitative ══════ */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Analyse qualitative</h3>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Analyse du marché</label>
          <textarea
            name="analyse_marche"
            rows={4}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
            value={val('analyse_marche')}
            onChange={handleChange('analyse_marche')}
            placeholder="Texte pré-rempli automatiquement par les données DVF..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Forces</label>
            <textarea
              name="forces"
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
              value={val('forces')}
              onChange={handleChange('forces')}
              placeholder="Points forts du bien..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Faiblesses</label>
            <textarea
              name="faiblesses"
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
              value={val('faiblesses')}
              onChange={handleChange('faiblesses')}
              placeholder="Points faibles du bien..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Opportunités</label>
            <textarea
              name="opportunites"
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
              value={val('opportunites')}
              onChange={handleChange('opportunites')}
              placeholder="Opportunités du marché..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Positionnement</label>
            <textarea
              name="positionnement"
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
              value={val('positionnement')}
              onChange={handleChange('positionnement')}
              placeholder="Positionnement du bien sur le marché local..."
            />
          </div>
        </div>
      </section>

      {/* ══════ Champs existants — État du marché ══════ */}
      <hr className="border-slate-200" />
      <h3 className="text-lg font-semibold text-slate-800">État du marché</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Select
          label="État du marché"
          name="etat_marche"
          value={val('etat_marche')}
          options={ETAT_MARCHE_OPTIONS}
          onChange={handleChange('etat_marche')}
        />
        <Select
          label="Évolution des prix"
          name="evolution_prix"
          value={val('evolution_prix')}
          options={EVOLUTION_PRIX_OPTIONS}
          onChange={handleChange('evolution_prix')}
        />
      </div>

      {/* Panel de transactions */}
      <h3 className="text-lg font-semibold text-slate-800">Panel de transactions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Input
          label="Nombre de transactions"
          name="panel_nombre_transactions"
          type="number"
          value={val('panel_nombre_transactions')}
          onChange={handleChange('panel_nombre_transactions')}
        />
        <Input
          label="Date début"
          name="panel_date_debut"
          type="date"
          value={val('panel_date_debut')}
          onChange={handleChange('panel_date_debut')}
        />
        <Input
          label="Date fin"
          name="panel_date_fin"
          type="date"
          value={val('panel_date_fin')}
          onChange={handleChange('panel_date_fin')}
        />
        <Input
          label="Rayon (km)"
          name="panel_rayon_km"
          type="number"
          value={val('panel_rayon_km')}
          onChange={handleChange('panel_rayon_km')}
        />
      </div>

      {/* Analyse par quartiles */}
      <h3 className="text-lg font-semibold text-slate-800">Analyse par quartiles (€/m²)</h3>
      <div className="overflow-x-auto">
        <table className="min-w-[320px] text-sm border border-slate-200 rounded-lg">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-4 py-2 text-left font-medium text-slate-700">Quartile</th>
              <th className="px-4 py-2 text-left font-medium text-slate-700">Min (€/m²)</th>
              <th className="px-4 py-2 text-left font-medium text-slate-700">Max (€/m²)</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((q) => (
              <tr key={q} className="border-t border-slate-200">
                <td className="px-4 py-2 font-medium text-slate-600">Q{q}</td>
                <td className="px-4 py-2">
                  <Input
                    name={`quartile_${q}_min`}
                    type="number"
                    value={val(`quartile_${q}_min`)}
                    onChange={handleChange(`quartile_${q}_min`)}
                  />
                </td>
                <td className="px-4 py-2">
                  <Input
                    name={`quartile_${q}_max`}
                    type="number"
                    value={val(`quartile_${q}_max`)}
                    onChange={handleChange(`quartile_${q}_max`)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Select
          label="Positionnement retenu"
          name="selection_quartiles"
          value={val('selection_quartiles')}
          options={SELECTION_QUARTILE_OPTIONS}
          onChange={handleChange('selection_quartiles')}
        />
      </div>

      {/* ══════ Transactions comparables ══════ */}
      <TransactionTable
        transactions={data?.transactions || []}
        etudeId={data?.id}
        onRefresh={onRefresh}
        onRefreshDvf={() => fetchDvf({ refresh: true })}
      />
    </div>
  );
}
